import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { Search as SearchIcon, Inventory as MaterialIcon } from "@mui/icons-material";
import MaterialService from "../services/Material.service";

const FILTERS = [
    { value: "ALL", label: "All" },
    { value: "RM", label: "Raw Materials" },
    { value: "AS", label: "Assemblies" },
];

function StatusChip({ status }) {
    const map = { active: "success", inactive: "default", draft: "warning", dev: "warning" };
    return (
        <Chip
            label={status ?? "—"}
            size="small"
            color={map[status] ?? "default"}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
        />
    );
}

export default function MaterialSearch() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("ALL");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searched, setSearched] = useState(false);
    const debounceRef = useRef(null);

    const runSearch = useCallback((term, type) => {
        if (!term || term.trim().length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        setLoading(true);
        setError("");
        const params = { search: term.trim(), page_size: 25 };

        const requests = [];
        if (type === "ALL" || type === "RM") {
            requests.push(
                MaterialService.searchRawMaterials(params).then((res) =>
                    (res.data.results ?? res.data).map((item) => ({ ...item, __type: "RM" }))
                )
            );
        }
        if (type === "ALL" || type === "AS") {
            requests.push(
                MaterialService.searchAssemblies(params).then((res) =>
                    (res.data.results ?? res.data).map((item) => ({ ...item, __type: "AS" }))
                )
            );
        }

        Promise.all(requests)
            .then((lists) => {
                setResults(lists.flat());
                setSearched(true);
            })
            .catch(() => setError("Search failed. Please try again."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(query, filter), 350);
        return () => clearTimeout(debounceRef.current);
    }, [query, filter, runSearch]);

    const openItem = (item) => {
        navigate(`/shop/material?type=${item.__type}&id=${item.id}`);
    };

    return (
        <Box sx={{ p: 2, maxWidth: 520, mx: "auto" }}>
            <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <MaterialIcon /> Find Material
                </Typography>

                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Part number, description, or lot"
                        placeholder="Start typing to search…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
                    />

                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        value={filter}
                        onChange={(_, v) => v && setFilter(v)}
                    >
                        {FILTERS.map((f) => (
                            <ToggleButton key={f.value} value={f.value}>
                                {f.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {error && <Alert severity="error">{error}</Alert>}

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={26} />
                        </Box>
                    ) : searched && results.length === 0 ? (
                        <Alert severity="warning">No matching materials or assemblies found.</Alert>
                    ) : results.length > 0 ? (
                        <List disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                            {results.map((item, idx) => (
                                <ListItemButton
                                    key={`${item.__type}-${item.id}`}
                                    onClick={() => openItem(item)}
                                    divider={idx < results.length - 1}
                                >
                                    <ListItemText
                                        primary={item.full_part_number ?? item.part_number}
                                        secondary={item.description}
                                    />
                                    <Stack alignItems="flex-end" spacing={0.5}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={item.__type === "RM" ? "Raw Material" : "Assembly"}
                                        />
                                        <StatusChip status={item.status} />
                                    </Stack>
                                </ListItemButton>
                            ))}
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                            Enter at least 2 characters to search.
                        </Typography>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
}
